import { Timeline } from "antd";
import classNames from "classnames";
import {
  MaterialDateConfirmationMilestone,
  MilestoneStatus,
  ProjectCalendarOffs
} from "../types";
import ActiveTimelineItem from "./active-timeline-item";
import CompletedTimelineItem from "./completed-timeline-item";
import PendingTimelineItem from "./pending-timeline-item";

type TimelineDotVariant = "green" | "blue" | "gray";

function getTimelineDotVariant(status: MilestoneStatus): TimelineDotVariant {
  switch (status) {
    case "completed":
      return "green";
    case "active":
      return "blue";
    default:
      return "gray";
  }
}

function TimelineMilestoneDot({ variant }: { variant: TimelineDotVariant }) {
  return (
    <span
      className={classNames(
        "timeline-milestone-dot",
        `timeline-milestone-dot--${variant}`
      )}
      aria-hidden
    />
  );
}

function MaterialTimeline({
  milestones,
  timezoneId,
  calendarOffs,
  projectedDateLabel,
  forceFirstMilestoneReadOnly = true,
  canMarkCompleteWhenNoPrevious = false,
  disableFirstMilestoneSetActual = false,
  projectedDateErrorIds,
  actualDateErrorIds,
  onMilestoneChange,
  onMarkMilestoneComplete
}: {
  milestones: MaterialDateConfirmationMilestone[];
  timezoneId: string;
  calendarOffs?: ProjectCalendarOffs;
  projectedDateLabel?: string;
  forceFirstMilestoneReadOnly?: boolean;
  canMarkCompleteWhenNoPrevious?: boolean;
  disableFirstMilestoneSetActual?: boolean;
  projectedDateErrorIds?: Set<string>;
  actualDateErrorIds?: Set<string>;
  onMilestoneChange: (
    milestoneId: string,
    updates: Partial<
      Pick<MaterialDateConfirmationMilestone, "projectedDate" | "actualDate">
    >
  ) => void;
  onMarkMilestoneComplete: (milestoneId: string, isComplete: boolean) => void;
}) {
  return (
    <Timeline className="material-date-confirmation-timeline pl-1">
      {milestones.map((milestone, index) => {
        const isReadOnlyFirstMilestone =
          forceFirstMilestoneReadOnly && milestone.order === 1;
        const isFirstMilestone = milestone.order === 1;
        const previousMilestone = milestones[index - 1];
        const canMarkComplete = previousMilestone
          ? Boolean(previousMilestone.actualDate)
          : canMarkCompleteWhenNoPrevious;
        const disableSetActualDate =
          isFirstMilestone && disableFirstMilestoneSetActual;
        const dotVariant = isReadOnlyFirstMilestone
          ? "green"
          : getTimelineDotVariant(milestone.status);
        return (
          <Timeline.Item
            key={milestone.id}
            dot={<TimelineMilestoneDot variant={dotVariant} />}
            data-testid={`material-timeline-item-${milestone.id}`}
          >
            {(isReadOnlyFirstMilestone || milestone.status === "completed") && (
              <CompletedTimelineItem milestone={milestone} />
            )}
            {!isReadOnlyFirstMilestone && milestone.status === "active" && (
              <ActiveTimelineItem
                milestone={milestone}
                previousMilestone={previousMilestone}
                timezoneId={timezoneId}
                calendarOffs={calendarOffs}
                canMarkComplete={canMarkComplete}
                projectedDateLabel={projectedDateLabel}
                disableSetActualDate={disableSetActualDate}
                hasProjectedDateError={projectedDateErrorIds?.has(milestone.id)}
                hasActualDateError={actualDateErrorIds?.has(milestone.id)}
                onProjectedDateChange={(projectedDate) => {
                  onMilestoneChange(milestone.id, { projectedDate });
                }}
                onCompletedDateChange={(actualDate) => {
                  onMilestoneChange(milestone.id, { actualDate });
                }}
                onMarkCompleteChange={(isComplete) => {
                  onMarkMilestoneComplete(milestone.id, isComplete);
                }}
              />
            )}
            {!isReadOnlyFirstMilestone && milestone.status === "pending" && (
              <PendingTimelineItem
                milestone={milestone}
                previousMilestone={previousMilestone}
                timezoneId={timezoneId}
                calendarOffs={calendarOffs}
                canMarkComplete={canMarkComplete}
                projectedDateLabel={projectedDateLabel}
                disableSetActualDate={disableSetActualDate}
                hasProjectedDateError={projectedDateErrorIds?.has(milestone.id)}
                hasActualDateError={actualDateErrorIds?.has(milestone.id)}
                onProjectedDateChange={(projectedDate) => {
                  onMilestoneChange(milestone.id, { projectedDate });
                }}
                onCompletedDateChange={(actualDate) => {
                  onMilestoneChange(milestone.id, { actualDate });
                }}
                onMarkCompleteChange={(isComplete) => {
                  onMarkMilestoneComplete(milestone.id, isComplete);
                }}
              />
            )}
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}

export default MaterialTimeline;
